import {
  DetailedHTMLProps,
  FunctionComponent,
  ImgHTMLAttributes,
  useMemo,
  useState,
} from 'react';

import { LOGO_SIZE } from 'src/constants/logoConsts';
import { getDummyImageURL, getPublicImageURL } from 'src/utils/ImageUtils';

interface Props {
  /** className for the container */
  containerClassName?: string;
  /** className for the icon */
  iconClassName?: string;
  /** URL to the workspace avatar image */
  iconSrc: string | null;
  /** size in pixels */
  size?: number;
  /** name of the workspace, needed for the fallback image */
  workspaceName: string;
}

/**
 * Shows the Avatar (logo) for a workspace. If the image fails to load or is
 * not a valid URL, a dummy image is shown with the first letter of the workspace name.
 * @param props.containerClassName className for the container
 * @param props.iconClassName className for the icon
 * @param props.iconSrc URL to the workspace avatar image
 * @param props.size size in pixels
 * @param props.workspaceName name of the workspace (needed for the fallback image)
 * @example
 * ```js
 * <WorkspaceAvatar
 *   iconSrc="https://example.com/avatar.png"
 *   workspaceName="Example Workspace"
 * />
 * ```
 */
export const WorkspaceAvatar: FunctionComponent<Props> = ({
  containerClassName = '',
  iconClassName = '',
  iconSrc: initialIconSrc,
  size = LOGO_SIZE,
  workspaceName,
}) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const containerProps = {
    className: `flex items-center ${containerClassName}`,
    style: { width: size, height: size },
  };

  const imgProps = useMemo(
    (): Pick<
      DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
      'className' | 'src' | 'srcSet'
    > => ({
      className:
        `rounded text-transparent w-full h-full ` +
        (isLoaded ? 'bg-transparent' : 'bg-slate-100') +
        ` ${iconClassName}`,
      // use the initial icon src if it is valid,
      // otherwise use the dummy image
      ...(initialIconSrc &&
      typeof initialIconSrc === 'string' &&
      initialIconSrc !== '' &&
      !hasError
        ? {
            src: getPublicImageURL(initialIconSrc ?? '', {
              resize: {
                width: size,
                height: size,
              },
            }),
            srcSet: `${getPublicImageURL(initialIconSrc ?? '', {
              resize: {
                width: size * 2,
                height: size * 2,
              },
            })} 2x`,
          }
        : {
            src: getDummyImageURL(workspaceName, { size }),
            srcSet: `${getDummyImageURL(workspaceName, { size: size * 2 })} 2x`,
          }),
    }),
    [hasError, iconClassName, initialIconSrc, isLoaded, size, workspaceName],
  );

  return (
    <div {...containerProps}>
      <img
        {...imgProps}
        alt={`${workspaceName ?? 'Workspace'} logo`}
        onError={!hasError ? () => setHasError(true) : undefined}
        onLoad={!isLoaded ? () => setIsLoaded(true) : undefined}
      />
    </div>
  );
};
